
import axios from 'axios';
import requestDeduplicationService from './requestDeduplicationService';

import { getAuthToken } from '../utils/authUtils'; //

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';
export const personalizedDashboardService = {
  /**
   * Load user's dashboard layout
   * @param {string} userId - The user ID
   * @returns {Promise<Array>} User's dashboard layout
   */
  async loadUserLayout(userId) {
    try {
      const token = await getAuthToken();
      const response = await axios.get(`${API_BASE}/internal/dashboard/layouts/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data.layout || [];
    } catch (error) {
      console.error('Error loading user layout:', error);
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return [];
      }
      throw error;
    }
  },

  /**
   * Save user's dashboard layout
   * @param {string} userId - The user ID
   * @param {Array} layout - The layout to save
   * @returns {Promise<Object>} Save result
   */
  async saveUserLayout(userId, layout) {
    try {
      const token = await getAuthToken();
      const response = await axios.put(`${API_BASE}/internal/dashboard/layouts/${userId}`, {
        layout,
        updatedAt: new Date()
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error saving user layout:', error);
      throw error;
    }
  },

  /**
   * Get unread messages for user
   * @param {string} userId - The user ID
   * @param {string} facilityId - The facility ID
   * @param {number} limit - Maximum number of messages to return
   * @returns {Promise<Array>} Array of unread messages
   */
  async getUnreadMessages(userId, facilityId, limit = 5) {
    try {
      const token = await getAuthToken();
      const response = await axios.get(`${API_BASE}/internal/dashboard/messages/unread`, {
        params: { userId, facilityId, limit },
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data.messages || [];
    } catch (error) {
      console.error('Error fetching unread messages:', error);
      return [];
    }
  },

  /**
   * Get pending tasks for user
   * @param {string} userId - The user ID
   * @param {number} limit - Maximum number of tasks to return
   * @returns {Promise<Array>} Array of pending tasks
   */
  async getPendingTasks(userId, limit = 5) {
    try {
      const token = await getAuthToken();
      const response = await axios.get(`${API_BASE}/internal/dashboard/tasks/pending`, {
        params: { userId, limit },
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data.tasks || [];
    } catch (error) {
      console.error('Error fetching pending tasks:', error);
      return [];
    }
  },

  /**
   * Get upcoming appointments for user
   * @param {string} userId - The user ID
   * @param {number} days - Number of days to look ahead
   * @param {number} limit - Maximum number of appointments to return
   * @returns {Promise<Array>} Array of upcoming appointments
   */
  async getUpcomingAppointments(userId, days = 7, limit = 5) {
    try {
      const token = await getAuthToken();
      const response = await axios.get(`${API_BASE}/internal/dashboard/appointments/upcoming`, {
        params: { userId, days, limit },
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data.appointments || [];
    } catch (error) {
      console.error('Error fetching upcoming appointments:', error);
      return [];
    }
  },

  /**
   * Get user favorites
   * @param {string} userId - The user ID
   * @returns {Promise<Array>} Array of user favorites
   */
  async getUserFavorites(userId) {
    try {
      const token = await getAuthToken();
      const response = await axios.get(`${API_BASE}/internal/dashboard/favorites/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data.favorites || [];
    } catch (error) {
      console.error('Error fetching user favorites:', error);
      return [];
    }
  },

  /**
   * Save user favorites
   * @param {string} userId - The user ID
   * @param {Array} favorites - Array of favorites to save
   * @returns {Promise<Object>} Response from the API
   */
  async saveUserFavorites(userId, favorites) {
    try {
      const token = await getAuthToken();
      const response = await axios.put(`${API_BASE}/internal/dashboard/favorites/${userId}`, {
        favorites
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error saving user favorites:', error);
      throw error;
    }
  },

  /**
   * Get all dashboard data for a user
   * @param {string} userId - The user ID
   * @returns {Promise<Object>} Object containing all dashboard data
   */
  async getAllDashboardData(userId) {
    try {
      // Check deduplication service for pending requests
      const cacheKey = requestDeduplicationService.generateCacheKey('GET', `${API_BASE}/internal/dashboard/data/${userId}`);
      
      if (requestDeduplicationService.isRequestPending(cacheKey)) {
        console.log('Dashboard data request already in progress, waiting for completion...');
        return await requestDeduplicationService.trackRequest(cacheKey);
      }

      // Check for cached response
      const cachedResponse = requestDeduplicationService.getCachedResponse(cacheKey);
      if (cachedResponse) {
        console.log('Using cached dashboard data response');
        return cachedResponse;
      }

      const token = await getAuthToken();
      const response = await axios.get(`${API_BASE}/internal/dashboard/data/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = response.data;
      
      // Cache the response for 1 minute (dashboard data changes moderately)
      requestDeduplicationService.cacheResponse(cacheKey, data, 60000);
      
      // Complete the tracked request
      requestDeduplicationService.completeRequest(cacheKey, data);
      
      return data;
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      
      // Complete the tracked request with error
      const cacheKey = requestDeduplicationService.generateCacheKey('GET', `${API_BASE}/internal/dashboard/data/${userId}`);
      requestDeduplicationService.completeRequest(cacheKey, null, error);
      
      throw error;
    }
  }
};
